/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Cup Design Page - Step-based Flow for Mobile
 * Updated: 2026-01-02
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import StartScreen from '../components/StartScreen';
import WardrobePanel from '../components/WardrobeModal';
import OutfitStack from '../components/OutfitStack';
import { DesignLayer, CaseModel } from '../types';
import Footer from '../components/Footer';
import { getFriendlyErrorMessage } from '../lib/utils';
import { modalApi } from '../services/modalApi';
import { CoffeeCupMockup } from '../components/CoffeeCupMockup';
import { generateCupMockup } from '../services/cupMockupService';
import { templateService } from '../services/templateService';
import { useSystemSettings } from '../hooks/useSystemSettings';
import CompletionPage from '../components/CompletionPage';
import MobileNav from '../components/MobileNav';
import { RadialProgress } from '../components/RadialProgress';
import { LOADING_CONFIG } from '../lib/loadingConfig';
import { Position } from '../components/PositionSelector';
import { indexedDBService } from '../services/indexedDbService';

const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const listener = (event: MediaQueryListEvent) => setMatches(event.matches);
    mediaQueryList.addEventListener('change', listener);
    return () => mediaQueryList.removeEventListener('change', listener);
  }, [query]);
  return matches;
};

type Step = 1 | 2 | 3 | 4 | 5;

const CupDesignPage: React.FC = () => {
  const navigate = useNavigate();
  const { settings } = useSystemSettings();

  // Step management
  const [currentStep, setCurrentStep] = useState<Step>(1);

  // Image data
  const [designImageUrl, setDesignImageUrl] = useState<string | null>(null);
  const [designImageId, setDesignImageId] = useState<string | null>(null);
  const [designImagePath, setDesignImagePath] = useState<string | null>(null);

  // Design state
  const [designHistory, setDesignHistory] = useState<DesignLayer[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<CaseModel[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<CaseModel | null>(null);
  const [engravingStyle] = useState<'monochrome' | 'grayscale'>('monochrome');
  const [position] = useState<Position>('center');
  const [customPrompt, setCustomPrompt] = useState<string>('');

  // New UI state
  const [printStyle] = useState<string>('CENTER_STICKER');
  const [removeBackground, setRemoveBackground] = useState(false);

  // Completion state
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  const isMobile = useMediaQuery('(max-width: 767px)');

  // Load coffee cup templates from Firestore
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templates = await templateService.getTemplates('coffee_cup');
        setAvailableTemplates(templates);
      } catch (err) {
        console.error("Failed to load coffee cup templates", err);
        setAvailableTemplates([]);
      }
    };
    fetchTemplates();
  }, []);

  const displayImageUrl = useMemo(() => {
    if (designHistory.length > 0 && designHistory[currentIndex]) {
      return designHistory[currentIndex].resultImage;
    }
    return designImageUrl;
  }, [designHistory, currentIndex, designImageUrl]);

  const activeCupIds = useMemo(() =>
    designHistory.map(layer => layer.caseModel?.id).filter(Boolean) as string[],
    [designHistory]
  );

  const handleDesignUploaded = (data: { imageId: string; filepath: string; previewUrl: string }) => {
    setDesignImageUrl(data.previewUrl);
    setDesignImageId(data.imageId);
    setDesignImagePath(data.filepath);
    setDesignHistory([]);
    setCurrentIndex(0);
    setCurrentStep(2);
  };

  const handleStartOver = async () => {
    // Cleanup IndexedDB before resetting
    if (designImageId) {
      try {
        await indexedDBService.deleteDesignImage(designImageId);
        console.log('[CupDesignPage] ✅ Cleaned up IndexedDB on start over');
      } catch (err) {
        console.warn('[CupDesignPage] Failed to cleanup IndexedDB:', err);
      }
    }

    setDesignImageUrl(null);
    setDesignImageId(null);
    setDesignImagePath(null);
    setDesignHistory([]);
    setCurrentIndex(0);
    setError(null);
    setCurrentStep(1);
  };

  const handleCupSelect = useCallback(async (cupFile: File, cupInfo: CaseModel) => {
    if (!designImageId || isLoading) return;

    setError(null);
    setIsLoading(true);
    setSelectedTemplate(cupInfo);
    setLoadingMessage(`Đang tạo mẫu trên ${cupInfo.name}...`);
    setCurrentStep(4);

    try {
      const isMerchandise = cupInfo.category === 'Merchandise';

      // Skip upload for now, use local ID. Upload happens at submission.
      let actualImageId = designImageId;
      let actualFilepath = designImagePath;

      if (isMerchandise) {
        const pseudoMockupId = `merch-${actualImageId}-${cupInfo.id}`;
        const newLayer: DesignLayer = {
          caseModel: cupInfo,
          resultImage: designImageUrl || '',
          mockupId: pseudoMockupId,
          mockupPath: actualFilepath,
          mockupImageData: designImageUrl,
          designImageId: actualImageId,
          designImagePath: actualFilepath
        };

        setDesignHistory(prev => {
          const newHistory = [...prev, newLayer];
          setCurrentIndex(newHistory.length - 1);
          return newHistory;
        });
      } else {
        // Client-side canvas rendering
        setLoadingMessage('Đang xử lý logo...');
        const { resultImage } = await generateCupMockup(
          designImageUrl!,
          cupInfo.cleanUrl || cupInfo.url,  // Use clean image if available, fallback to regular
          cupInfo.maskUrl,                  // Mask image (optional)
          {
            removeBackground,
            customPrompt,
            printStyle
          }
        );

        // Upload mockup image immediately after rendering
        setLoadingMessage('Đang tải mockup lên server...');
        const mockupResp = await modalApi.uploadImage(resultImage, undefined, undefined, 'mockup');
        if (!mockupResp.success) {
          throw new Error('Failed to upload mockup image');
        }
        const mockupImagePath = mockupResp.filepath;
        console.log('[CupDesignPage] ✅ Mockup image uploaded:', mockupImagePath);

        // Charge billing after successful mockup upload
        setLoadingMessage('Đang xác nhận và lưu thiết kế...');

        // Get or generate visitor ID
        let visitorId = localStorage.getItem('veritas_visitor_id');
        if (!visitorId) {
          visitorId = crypto.randomUUID();
          localStorage.setItem('veritas_visitor_id', visitorId);
        }

        // Get or generate session ID
        let sessionId = localStorage.getItem('veritas_session_id');
        if (!sessionId) {
          sessionId = crypto.randomUUID();
          localStorage.setItem('veritas_session_id', sessionId);
        }

        // CRITICAL: Charge billing with actual mockup path - if this fails, the entire operation fails
        const chargeResult = await modalApi.chargeCupMockup({
          image_id: actualImageId,
          template_id: cupInfo.id,
          visitor_id: visitorId,
          session_id: sessionId,
          mockup_path: mockupImagePath  // Use actual uploaded path instead of placeholder
        });

        const mockupId = chargeResult.mockup_id;
        console.log('[CupDesignPage] ✅ Billing charged successfully, mockup_id:', mockupId);

        const newLayer: DesignLayer = {
          caseModel: cupInfo,
          resultImage: resultImage,
          mockupImageData: null,  // No need to store base64 since already uploaded
          designImageId: actualImageId,
          designImagePath: actualFilepath,
          mockupId: mockupId,  // Add mockup_id from billing
          mockupPath: mockupImagePath  // Use actual uploaded path
        };
        setDesignHistory(prev => {
          const newHistory = [...prev, newLayer];
          setCurrentIndex(newHistory.length - 1);
          return newHistory;
        });
      }
    } catch (err) {
      console.error("❌ Error in handleCupSelect:", err);
      setError(getFriendlyErrorMessage(err, 'Tạo mẫu in logo thất bại. Vui lòng thử lại'));
      setCurrentStep(3);
    } finally {
      setIsLoading(false);
      setSelectedTemplate(null);
    }
  }, [designImagePath, designImageId, isLoading, engravingStyle, position, customPrompt, removeBackground, printStyle, designImageUrl]);

  const handleRemoveLayer = () => {
    if (designHistory.length > 0) {
      setDesignHistory(prev => {
        const newHistory = prev.slice(0, -1);
        setCurrentIndex(Math.max(0, newHistory.length - 1));
        return newHistory;
      });
    }
  };

  const handleSubmit = async () => {
    if (!designHistory[currentIndex]) return;

    setIsLoading(true);
    setError(null);
    setLoadingMessage('Đang lưu lại thiết kế của bạn...');

    try {
      const layer = designHistory[currentIndex];

      // Ensure we have mockup data
      if (!layer.mockupId || !layer.mockupPath) {
        throw new Error('Thiết kế chưa hoàn tất. Vui lòng chọn lại mẫu sản phẩm.');
      }

      let visitorId = localStorage.getItem('veritas_visitor_id');
      if (!visitorId) {
        visitorId = crypto.randomUUID();
        localStorage.setItem('veritas_visitor_id', visitorId);
      }

      let sessionId = localStorage.getItem('veritas_session_id');
      if (!sessionId) {
        sessionId = crypto.randomUUID();
        localStorage.setItem('veritas_session_id', sessionId);
      }

      // 1. Lazy upload design image if needed
      let actualImageId = designImageId!;
      let actualFilepath = designImagePath!;

      if (!designImagePath) {
        console.log('[CupDesignPage] 📤 Lazy uploading design image...');
        setLoadingMessage('Đang tải ảnh gốc lên server...');

        const cached = await indexedDBService.getDesignImage(designImageId!);
        if (!cached) {
          throw new Error('Design image not found in cache');
        }

        // Optimization: Check if already uploaded
        if (cached.uploaded && cached.uploadedImageId && cached.uploadedFilepath) {
           console.log('[CupDesignPage] ✅ Using cached upload metadata from IndexedDB');
           actualImageId = cached.uploadedImageId;
           actualFilepath = cached.uploadedFilepath;
        } else {
           const uploadResp = await modalApi.uploadImage(cached.base64);
           if (uploadResp.success) {
             actualImageId = uploadResp.image_id;
             actualFilepath = uploadResp.filepath;
   
             await indexedDBService.markAsUploaded(
               designImageId!,
               uploadResp.image_id,
               uploadResp.filepath
             );
             console.log('[CupDesignPage] ✅ Design image uploaded');
           } else {
             throw new Error('Failed to upload design image');
           }
        }
      }

      // 2. Mockup already uploaded during cup selection, just verify we have the path
      let mockupId = layer.mockupId;
      let mockupPath = layer.mockupPath;

      if (!mockupId || !mockupPath) {
        throw new Error('Mockup data missing. Please select cup template again.');
      }

      console.log('[CupDesignPage] ✅ Using existing mockup:', { mockupId, mockupPath });

      // 3. Create submission
      setLoadingMessage('Đang lưu thiết kế...');
      const result = await modalApi.createSubmission({
        imageId: actualImageId,
        originalImagePath: actualFilepath,
        mockupId: mockupId!,
        mockupImagePath: mockupPath!,
        productType: 'coffee_cup',
        visitorId,
        sessionId,
        designOptions: {
          engravingStyle,
          removeBackground,
          printStyle
        },
        templateId: layer.caseModel?.id,
        templateName: layer.caseModel?.name
      });

      if (result.success) {
        setSubmissionId(result.submissionId);
        setCurrentStep(5);

        // Cleanup: Delete design image from IndexedDB after successful submission
        if (designImageId) {
          try {
            await indexedDBService.deleteDesignImage(designImageId);
            console.log('[CupDesignPage] ✅ Cleaned up IndexedDB after submission');
          } catch (cleanupErr) {
            console.warn('[CupDesignPage] Failed to cleanup IndexedDB:', cleanupErr);
            // Non-critical error, don't throw
          }
        }
      }
    } catch (err) {
      console.error("❌ Submission failed:", err);
      setError(getFriendlyErrorMessage(err, 'Gửi thiết kế thất bại. Vui lòng thử lại.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as Step);
    }
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep((currentStep + 1) as Step);
    }
  };

  const renderStep1 = () => (
    <div className="flex flex-col items-center justify-center h-full p-4">
      <StartScreen onDesignReady={handleDesignUploaded} mode="cup" />
    </div>
  );

  const renderStep2 = () => (
    <div className="flex flex-col md:flex-row min-h-full w-full overflow-auto relative bg-slate-50">
      <div className="md:hidden sticky top-0 z-30 w-full h-40 bg-slate-100 border-b border-slate-200 flex items-center justify-center p-4">
        {designImageUrl && <img src={designImageUrl} alt="Logo Preview" className="h-full w-auto object-contain rounded-lg shadow-sm" />}
      </div>

      <div className="hidden md:flex md:w-1/2 h-full items-center justify-center bg-slate-50 p-6">
        {designImageUrl && (
          <div className="relative w-full max-w-md">
            <img src={designImageUrl} alt="Logo Preview" className="w-full h-auto rounded-xl shadow-2xl max-h-[500px] object-contain border border-white/20" />
          </div>
        )}
      </div>

      <div className="flex-1 md:w-1/2 min-h-full flex flex-col bg-white overflow-auto md:border-l border-slate-200">
        <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-6 pb-24 mobile-scroll-smooth">
          {error && <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm border border-red-100 flex items-center gap-2">⚠️ {error}</div>}

          <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <span className="p-2 bg-emerald-100 rounded-lg text-lg">☕</span>
              <p className="text-base font-bold text-emerald-900 uppercase tracking-tight">Cốc Bã Cà Phê</p>
            </div>
            <p className="text-xs text-emerald-700 leading-relaxed">Cốc làm từ bã cà phê tái chế, màu nâu đậm tự nhiên. Logo sẽ được khắc laser màu <strong>vàng/gold</strong> nổi bật.</p>
          </div>
        </div>

        <div className="sticky bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-xl border-t border-slate-100 z-40 md:static md:bg-transparent md:border-none md:p-8">
          <button onClick={handleNext} className="w-full bg-emerald-600 text-white font-bold py-4 rounded-2xl hover:bg-emerald-700 transition-all shadow-xl active:scale-95 text-base uppercase tracking-widest">Tiếp tục</button>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="flex flex-col md:flex-row min-h-full w-full overflow-auto relative bg-slate-50">
      <div className="md:hidden sticky top-0 z-30 w-full h-40 bg-slate-100 border-b border-slate-200 flex items-center justify-center p-4">
        {designImageUrl && <img src={designImageUrl} alt="Logo Preview" className="h-full w-auto object-contain rounded-lg shadow-sm" />}
      </div>

      <div className="hidden md:flex md:w-1/2 h-full items-center justify-center bg-slate-50 p-6">
        {designImageUrl && (
          <div className="relative w-full max-w-md">
            <img src={designImageUrl} alt="Logo Preview" className="w-full h-auto rounded-xl shadow-2xl max-h-[500px] object-contain border border-white/20" />
          </div>
        )}
      </div>

      <div className="flex-1 md:w-1/2 min-h-full flex flex-col bg-white overflow-auto md:border-l border-slate-200">
        <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-6 pb-24 mobile-scroll-smooth">
          {error && <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm border border-red-100 mb-4">{error}</div>}
          <div className="content-area">
            {availableTemplates.length === 0 ? (
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-8 text-center text-amber-800">
                <p className="font-medium">⚠️ Chưa có mẫu cốc nào trong hệ thống.</p>
              </div>
            ) : (
              <WardrobePanel onGarmentSelect={handleCupSelect} activeGarmentIds={activeCupIds} isLoading={isLoading} wardrobe={availableTemplates} />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const currentTemplateImage = (designHistory[currentIndex]?.caseModel?.url) || (selectedTemplate?.url) || (availableTemplates[0]?.url);

    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center p-4 bg-slate-50">
          <div className="w-full max-w-2xl">
            <CoffeeCupMockup designImage={displayImageUrl} templateImage={currentTemplateImage} isLoading={isLoading} loadingMessages={LOADING_CONFIG.mockup.cup} />
          </div>
        </div>

        <div className="p-4 bg-white border-t border-slate-200 space-y-4">
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={isLoading || designHistory.length === 0}
              className="w-full bg-emerald-600 text-white font-bold py-4 rounded-lg hover:bg-emerald-700 transition-all shadow-lg active:scale-98 min-h-[44px] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? 'Đang gửi...' : 'Xác nhận thiết kế'}
            </button>
          </div>

          {designHistory.length > 0 && <OutfitStack history={designHistory} onRemoveLast={handleRemoveLayer} />}
        </div>
      </div>
    );
  };

  const renderStep5 = () => {
    if (!submissionId || !designHistory[currentIndex]) return null;
    return (
      <CompletionPage
        designInfo={{
          orderId: submissionId,
          productType: 'coffee_cup',
          productName: designHistory[currentIndex].caseModel?.name,
          mockupImageUrl: displayImageUrl || '',
          designOptions: { removeBackground, printStyle }
        }}
        shopUrl="https://custom.airxcoffee.com/cart"
      />
    );
  };

  return (
    <div className="font-sans min-h-screen bg-white flex flex-col">
      <MobileNav showDesignButton={false} />
      {settings?.announcement_banner && (
        <div className="bg-slate-700 text-white text-center py-2 px-4 text-sm font-medium relative z-40">📢 {settings.announcement_banner}</div>
      )}

      <div className="flex flex-col min-h-[calc(100vh-56px)] bg-white flex-1">
        {currentStep < 5 && (
          <div className="w-full bg-white border-b border-slate-100 py-3 flex justify-center sticky top-0 z-30">
            <RadialProgress currentStep={currentStep} totalSteps={4} color="#059669" size={isMobile ? 60 : 80} />
          </div>
        )}

        {currentStep > 1 && currentStep < 5 && (
          <div className="absolute top-[84px] left-4 z-30 md:top-20">
            <button onClick={handleBack} className="flex items-center justify-center gap-2 bg-white/95 backdrop-blur-sm border border-slate-200 text-slate-700 font-medium py-2.5 px-4 rounded-lg transition-all duration-200 hover:bg-white hover:shadow-md active:scale-95 min-h-[44px]">
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Quay lại</span>
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            {currentStep === 1 && <motion.div key="step-1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="h-full">{renderStep1()}</motion.div>}
            {currentStep === 2 && <motion.div key="step-2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="h-full">{renderStep2()}</motion.div>}
            {currentStep === 3 && <motion.div key="step-3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="h-full">{renderStep3()}</motion.div>}
            {currentStep === 4 && <motion.div key="step-4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="h-full">{renderStep4()}</motion.div>}
            {currentStep === 5 && <motion.div key="step-5" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3 }} className="h-full overflow-y-auto">{renderStep5()}</motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {currentStep < 5 && <Footer isOnDressingScreen={currentStep > 1} />}
    </div>
  );
};

export default CupDesignPage;